import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
  Spinner,
} from 'reactstrap'
import { Formik, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { convertQuantity } from '../../helpers/unitConversion'
import {
  FaInfoCircle,
  FaCheck,
  FaArrowLeft,
  FaFileAlt,
  FaMapMarkerAlt,
  FaBoxOpen,
  FaMoneyBillWave,
  FaPercent,
} from 'react-icons/fa'
import { submitProposal } from '../../store/slices/transportRequestSlice'

const RATE_UNIT_OPTIONS = [
  { value: 'Quintal', label: 'Per Quintal' },
  { value: 'Metric Ton', label: 'Per Metric Ton' },
  { value: 'KG', label: 'Per KG' },
  { value: 'Liter', label: 'Per Liter' },
]

const SubmitProposal = ({ isOpen, toggle, requestId, requestData }) => {
  const dispatch = useDispatch()
  const [advanceType, setAdvanceType] = useState('percentage')
  const [step, setStep] = useState('form')      // 'form' | 'preview'
  const [previewData, setPreviewData] = useState(null)

  const calculateTotals = (transportRate, rateUnit, advancePercentage, advanceAmount, advanceType) => {
    const rate = parseFloat(transportRate) || 0
    const quantity = parseFloat(requestData?.total_quantity) || 0
    const quantityUnit = requestData?.quantity_unit || ''
    const convertedQuantity = convertQuantity(quantity, quantityUnit, rateUnit)
    const totalFreight = rate * convertedQuantity
    
    let calculatedAdvanceAmount = 0
    let calculatedAdvancePercentage = 0
    
    if (advanceType === 'percentage') {
      const percentage = parseFloat(advancePercentage) || 0
      calculatedAdvanceAmount = (totalFreight * percentage) / 100
      calculatedAdvancePercentage = percentage
    } else {
      const amount = parseFloat(advanceAmount) || 0
      calculatedAdvanceAmount = amount
      calculatedAdvancePercentage = totalFreight > 0 ? (amount / totalFreight) * 100 : 0
    }
    
    return { totalFreight, advanceAmount: calculatedAdvanceAmount, advancePercentage: calculatedAdvancePercentage }
  }

  const validationSchema = Yup.object().shape({
    transport_rate: Yup.number()
      .required('Transport rate is required')
      .positive('Transport rate must be positive')
      .min(1, 'Transport rate must be at least 1'),
    rate_unit: Yup.string()
      .required('Rate unit is required'),
    advance_percentage: Yup.number()
      .when('$advanceType', {
        is: 'percentage',
        then: schema => schema
          .required('Advance percentage is required')
          .min(0, 'Advance percentage cannot be negative')
          .max(100, 'Advance percentage cannot exceed 100'),
        otherwise: schema => schema.notRequired()
      }),
    advance_amount: Yup.number()
      .when('$advanceType', {
        is: 'amount',
        then: schema => schema
          .required('Advance amount is required')
          .min(0, 'Advance amount cannot be negative'),
        otherwise: schema => schema.notRequired()
      }),
    payment_terms: Yup.string()
      .required('Payment terms are required')
      .min(10, 'Payment terms must be at least 10 characters'),
  })

  const initialValues = {
    transport_rate: '',
    rate_unit: RATE_UNIT_OPTIONS[0].value,
    advance_percentage: '',
    advance_amount: '',
    payment_terms: '',
  }

  // Step 1: validate and go to preview
  const handleReview = (values, { setSubmitting }) => {
    const { totalFreight, advanceAmount, advancePercentage } = calculateTotals(
      values.transport_rate,
      values.rate_unit,
      values.advance_percentage,
      values.advance_amount,
      advanceType
    )
    const qty = parseFloat(requestData?.total_quantity) || 0
    const qtyUnit = requestData?.quantity_unit || ''
    const converted = convertQuantity(qty, qtyUnit, values.rate_unit)

    setPreviewData({
      ...values,
      advanceType,
      totalFreight: Math.round(totalFreight * 100) / 100,
      advanceAmount: Math.round(advanceAmount * 100) / 100,
      advancePercentage: Math.round(advancePercentage * 100) / 100,
      originalQty: qty,
      originalUnit: qtyUnit,
      convertedQty: converted,
    })
    setStep('preview')
    setSubmitting(false)
  }

  // Step 2: actually submit
  const handleConfirmSubmit = async (setSubmitting, resetForm) => {
    if (!previewData) return
    setSubmitting(true)
    try {
      const proposalData = {
        transport_rate: parseFloat(previewData.transport_rate),
        rate_unit: previewData.rate_unit,
        advance_percentage: previewData.advancePercentage,
        advance_amount: previewData.advanceAmount,
        total_freight_cost: previewData.totalFreight,
        payment_terms: previewData.payment_terms,
      }
      await dispatch(submitProposal({ requestId, proposalData })).unwrap()
      resetForm()
      setAdvanceType('percentage')
      setStep('form')
      setPreviewData(null)
      toggle()
    } catch (_) {
      // toast already shown by slice
    } finally {
      setSubmitting(false)
    }
  }

  const handleModalClose = () => {
    setStep('form')
    setPreviewData(null)
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose} size="lg" className="aurora-modal">
      <ModalHeader toggle={handleModalClose} className="border-0">
        <span className="neo-text">
          {step === 'form' ? 'Submit Freight Proposal' : 'Review Your Proposal'}
        </span>
      </ModalHeader>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        context={{ advanceType }}
        onSubmit={handleReview}
      >
        {({ values, handleSubmit, isSubmitting, setFieldValue, setSubmitting, resetForm }) => {
          const { totalFreight, advanceAmount, advancePercentage } = calculateTotals(
            values.transport_rate,
            values.rate_unit,
            values.advance_percentage,
            values.advance_amount,
            advanceType
          )

          /* ── STEP 1: FORM ── */
          if (step === 'form') return (
            <Form onSubmit={handleSubmit}>
              <ModalBody className="p-4">
                <div className="alert-glass-info mb-4 p-3 rounded-3 d-flex align-items-center">
                  <FaInfoCircle className="me-3 text-info" size={20} />
                  <div>
                    <h6 className="mb-0 fw-bold">Proposal for {requestData?.request_number}</h6>
                    <small>Quantity: {requestData?.total_quantity} {requestData?.quantity_unit} &nbsp;·&nbsp; {requestData?.origin} → {requestData?.destination}</small>
                  </div>
                </div>

                <Row className="g-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label className="detail-label">Rate (₹)</Label>
                      <Field
                        name="transport_rate"
                        type="number"
                        className="glass-input form-control"
                        placeholder="e.g. 2400"
                      />
                      <ErrorMessage name="transport_rate" component="div" className="text-danger small mt-1" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="detail-label">Rate Unit</Label>
                      <Field name="rate_unit" as="select" className="glass-input form-control">
                        {RATE_UNIT_OPTIONS.map(unit => (
                          <option key={unit.value} value={unit.value}>{unit.label}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="rate_unit" component="div" className="text-danger small mt-1" />
                    </FormGroup>
                  </Col>
                </Row>

                <div className="mb-3">
                  <Label className="detail-label">Advance Payment Option</Label>
                  <div className="d-flex gap-4">
                    <label className="d-flex align-items-center cursor-pointer mb-0">
                      <input type="radio" className="custom-radio me-2"
                        checked={advanceType === 'percentage'}
                        onChange={() => { setAdvanceType('percentage'); setFieldValue('advance_amount', '') }}
                      />
                      <span className="text-main small">Percentage (%)</span>
                    </label>
                    <label className="d-flex align-items-center cursor-pointer mb-0">
                      <input type="radio" className="custom-radio me-2"
                        checked={advanceType === 'amount'}
                        onChange={() => { setAdvanceType('amount'); setFieldValue('advance_percentage', '') }}
                      />
                      <span className="text-main small">Fixed Amount (₹)</span>
                    </label>
                  </div>
                </div>

                <Row className="mb-3">
                  <Col md={12}>
                    <FormGroup className="mb-0">
                      <Label className="detail-label">{advanceType === 'percentage' ? 'Advance (%)' : 'Advance Amount (₹)'}</Label>
                      <Field
                        name={advanceType === 'percentage' ? 'advance_percentage' : 'advance_amount'}
                        type="number"
                        className="glass-input form-control"
                        placeholder={advanceType === 'percentage' ? 'e.g. 30' : 'e.g. 15000'}
                      />
                      <ErrorMessage name={advanceType === 'percentage' ? 'advance_percentage' : 'advance_amount'} component="div" className="text-danger small mt-1" />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup className="mb-4">
                  <Label className="detail-label">Payment Terms</Label>
                  <Field name="payment_terms" as="textarea" rows="3"
                    className="glass-input form-control"
                    placeholder="e.g. 30% advance, 70% on delivery..."
                  />
                  <ErrorMessage name="payment_terms" component="div" className="text-danger small mt-1" />
                </FormGroup>

                {/* Live calc preview */}
                <div className="proposal-summary p-3 glass-effect rounded-3 border-glass">
                  <h6 className="neo-text mb-3">Live Estimate</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Rate</span>
                        <span className="value">₹{parseFloat(values.transport_rate || 0).toLocaleString()} <small>/ {values.rate_unit}</small></span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Qty Conversion</span>
                        <span className="value small text-muted">
                          {(() => {
                            const qty = parseFloat(requestData?.total_quantity) || 0
                            const qtyUnit = requestData?.quantity_unit || ''
                            const conv = convertQuantity(qty, qtyUnit, values.rate_unit)
                            return `${qty} ${qtyUnit} = ${conv.toLocaleString()} ${values.rate_unit}`
                          })()}
                        </span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Total Freight</span>
                        <span className="value text-primary fw-bold">₹{totalFreight.toLocaleString()}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Advance ({advanceType === 'percentage' ? `${advancePercentage.toFixed(1)}%` : 'Fixed'})</span>
                        <span className="value text-success fw-bold">₹{advanceAmount.toLocaleString()}</span>
                      </div>
                    </Col>
                  </Row>
                </div>
              </ModalBody>

              <ModalFooter className="border-0">
                <Button color="secondary" className="glass-effect" onClick={handleModalClose}>Cancel</Button>
                <Button color="primary" className="btn-gradient" type="submit">
                  Review Proposal →
                </Button>
              </ModalFooter>
            </Form>
          )

          /* ── STEP 2: PREVIEW ── */
          const p = previewData
          return (
            <div>
              <ModalBody className="p-4">
                <div className="alert-glass-info mb-4 p-3 rounded-3 d-flex align-items-center">
                  <FaFileAlt className="me-3 text-primary" size={20} />
                  <div>
                    <h6 className="mb-0 fw-bold">Please confirm all details before submitting</h6>
                    <small className="text-muted">Once submitted, the transporter will receive your proposal.</small>
                  </div>
                </div>

                {/* Request Info */}
                <div className="mb-3 p-3 rounded-3" style={{ background: 'var(--bg, #f4f6fb)', border: '1px solid var(--border, #e8edf2)' }}>
                  <div className="fw-bold small text-muted mb-2 text-uppercase" style={{ letterSpacing: '0.06em' }}>Request Details</div>
                  <Row className="g-2">
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Request No: </span>
                        <span className="value">{requestData?.request_number}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label">Quantity: </span>
                        <span className="value">{requestData?.total_quantity} {requestData?.quantity_unit}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label"><FaMapMarkerAlt className="me-1" />Origin: </span>
                        <span className="value">{requestData?.origin || '—'}</span>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mini-detail">
                        <span className="label"><FaMapMarkerAlt className="me-1" />Destination: </span>
                        <span className="value">{requestData?.destination || '—'}</span>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Proposal Terms */}
                <div className="mb-3 p-3 rounded-3" style={{ background: 'var(--bg, #f4f6fb)', border: '1px solid var(--border, #e8edf2)' }}>
                  <div className="fw-bold small text-muted mb-2 text-uppercase" style={{ letterSpacing: '0.06em' }}>Your Proposal</div>
                  <Row className="g-2">
                    <Col md={4}>
                      <div className="mini-detail">
                        <span className="label">Rate: </span>
                        <span className="value fw-bold">₹{parseFloat(p?.transport_rate || 0).toLocaleString()}</span>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mini-detail">
                        <span className="label">Rate Unit: </span>
                        <span className="value  fw-bold">{p?.rate_unit}</span>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mini-detail">
                        <span className="label">Qty Conversion: </span>
                        <span className="value small text-muted">
                          {`${p?.originalQty} ${p?.originalUnit} = ${(p?.convertedQty || 0).toLocaleString()} ${p?.rate_unit}`}
                        </span>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mini-detail">
                        <span className="label">Advance Type: </span>
                        <span className="value  fw-bold">{p?.advanceType === 'percentage' ? `${p?.advancePercentage}%` : 'Fixed Amount'}</span>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mini-detail">
                        <span className="label">Advance Amount: </span>
                        <span className="value text-success fw-bold">₹{(p?.advanceAmount || 0).toLocaleString()}</span>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mini-detail">
                        <span className="label">Payment Terms: </span>
                        <span className="value small">{p?.payment_terms}</span>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Total highlight */}
                <div className="p-3 rounded-3 text-center" style={{ background: 'linear-gradient(135deg, rgba(75,99,205,0.08), rgba(46,58,122,0.12))', border: '1.5px solid rgba(75,99,205,0.2)' }}>
                  <div className="small text-muted mb-1">Total Freight Cost</div>
                  <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--primary, #2e3a7a)', letterSpacing: '-0.03em' }}>
                    ₹{(p?.totalFreight || 0).toLocaleString()}
                  </div>
                </div>
              </ModalBody>

              <ModalFooter className="border-0">
                <Button color="secondary" className="glass-effect" onClick={() => setStep('form')} disabled={isSubmitting}>
                  <FaArrowLeft className="me-1" /> Edit
                </Button>
                <Button
                  color="primary" className="btn-gradient"
                  disabled={isSubmitting}
                  onClick={() => handleConfirmSubmit(setSubmitting, resetForm)}
                >
                  {isSubmitting ? <Spinner size="sm" className="me-2" /> : <FaCheck className="me-2" />}
                  Confirm & Submit
                </Button>
              </ModalFooter>
            </div>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default SubmitProposal
